import VideoRecorder from "./VideoRecorder.js"
import LevelBuilder from '../level/LevelBuilder.js';
import Player from "../entity/Player.js"
import SoundController from "./SoundController.js"
import CameraManager from "./CameraManager.js"
import { getElem, pause} from "../utils/index.js";
import HTML_GameOver from "../_html/HTML_GameOver";
import HTML_UI from "../_html/HTML_UI.js";
import TutorialManager from "./TutorialManager.js";


class GameManager {
    constructor(config, app) {
        this.config = config;
        
        // Game state
        this.app = app;
        this.engine = Matter.Engine.create({
            gravity: {
                x: 0,
                y: this.config.gravity
            }
        });
        
        this.runner = Matter.Runner.create();
        Matter.Runner.run(this.runner, this.engine);

        this.gameStarted = false;
        this.gameOver = false;
        this.respawning = false;
        
        // Components
        this.cameraManager = new CameraManager(this.app);
        this.soundController = new SoundController(this.config);
        this.levelBuilder = new LevelBuilder(this.app, this.engine, this.config);
        this.player = new Player(this.app, this.engine, this.config);
        this.tutorial = new TutorialManager(this.config, this.app, this.engine);
        this.videoRecorder = new VideoRecorder();
        
        // Level data
        this.gameObjects = [];
        this.hazards = [];
        this.pickups = [];
        this.systems = [];
        
        this.collidedHazards = new Set();
        this.collidedPickups = new Set();
        this.collidedSystems = new Set();
        
        this.html_GameOver = new HTML_GameOver();
        this.html_UI = new HTML_UI();
        
        this.respawnSpot = 0;
        
        // Level tracking
        this.levelWidth = 0;
    }

    async initCam() {
        // Set a flag to indicate we'll handle camera after first user interaction
        const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

        await this.cameraManager.setupCamera();
        // if (!isIOS) {
        //     // Non-iOS devices - try camera setup immediately
        //     await this.cameraManager.setupCamera();
        // } else {
        //     // For iOS - wait for first interaction before attempting camera setup
        //     console.log("iOS detected: will set up camera after user interaction");
            
        //     // Set up one-time click handler for iOS
        //     const setupIOSCamera = async () => {
        //         await this.cameraManager.setupCamera(); // camera not properly setting up here why do I need to wait for click
        //         document.removeEventListener('click', setupIOSCamera);
        //     };
            
        //     document.addEventListener('click', setupIOSCamera);
        // }
    }

    async initMic() {
        try {
            // Continue with the rest of game initialization
            this.audioStream = await this.soundController.setupMicrophone();
            return true;
        } catch (error) {
            console.error('Error initializing game:', error);
            getElem('mic-error').style.display = 'block';
            return false;
        }
    }

    async playTutorial() {
        await this.tutorial.initialize(this.soundController, () => {
            this.playGame();
        })
    }

    async playGame() {
        // tutorial
        this.levelWidth = this.levelBuilder.createCosmetics();
        this.player.create(() => {
            this.respawning = false;
        });
        
        await this.videoRecorder.initialize(this.audioStream, this.app.view);
        
        // Update sky background with correct level width if needed
        if (!this.cameraManager.cameraSprite && this.levelWidth > 0) {
            this.cameraManager.createSkyBackground(this.levelWidth);
        }
        
        // Start countdown
        this.startCountdown();
    }

    startCountdown() {
        this.soundController.toggleSoundBar(true);
        this.app.ticker.add(this.gameLoop.bind(this));
        // Reset player velocity to ensure they don't move during countdown
        if (this.player && this.player.body) {
            Matter.Body.setVelocity(this.player.body, { x: 0, y: 0 });
        }
        
        // Set global flag to prevent player movement
        window.gameStarted = false;
        this.cleanupLevel();

        const levelData = this.levelBuilder.createLevel();
        this.gameObjects = levelData.gameObjects;
        this.hazards = levelData.hazards;
        this.pickups = levelData.pickups;
        this.systems = levelData.systems;

        this.html_UI.startCountdown(() => {
            this.startGame();
        });
    }

    startGame() {
        this.gameStarted = true;
        this.gameOver = false;
        
        // Set global flag for player movement
        window.gameStarted = true;
        
        // Start recording
        this.videoRecorder.startRecording();
    }

    async endGame(wonGame) {
        if (this.gameOver) return;
        
        this.soundController.toggleSoundBar(false);
        this.gameOver = true;
        this.html_UI.pause();
        this.html_UI.dismiss();
        const stats = this.html_UI.getStats();
        
        // Stop player movement
        Matter.Body.setVelocity(this.player.body, { x: 0, y: 0 });
        
        if(wonGame) {
            await pause(2000);
        }
        // Stop recording and show download prompt
        this.videoRecorder.stopRecording().then(blob => {
            if (blob) {
                this.html_GameOver.activate(wonGame, stats, blob, () => {
                    this.resetGame();
                });
            } else {
                console.error('Failed to get recording blob');
            }
        });
    }

    onDeath() {
        this.gameStarted = false;
        let shouldDie = this.html_UI.loseLife();
        if(shouldDie) {
            this.endGame(false);
        } else {
            setTimeout(() => {
                this.gameStarted = true;
                if(this.videoRecorder.getRecordingDurationCurrent() / 1000 > 13) {
                    // Ensure recording is at least 12 seconds long before terminating
                    this.videoRecorder.pauseRecording();
                }
                this.player.reset(this.respawnSpot);
                this.app.stage.x = this.respawnSpot;
    
                if (this.player && this.player.body) {
                    Matter.Body.setVelocity(this.player.body, { x: 0, y: 0 });
                }
    
                this.respawning = true;
            }, 1000)
        }
    }
    
    resetGame() {
        // Reset player
        this.respawnSpot = 0;
        this.player.reset();
        this.html_UI.reset();
        
        // Reset camera position
        this.app.stage.x = 0;
        
        // Reset game state
        this.gameOver = false;
        
        // Start countdown and game
        this.startCountdown();
    }

    
    gameLoop() {
        // Update sound level
        const soundLevel = this.soundController.updateSoundLevel();
        
        // Handle player movement based on sound - only if game has started
        if (!this.respawning && this.gameStarted && !this.gameOver) {
            this.player.handleMovement(soundLevel);
        } else {
            // Ensure player doesn't move during countdown
            if (this.player && this.player.body) {
                Matter.Body.setVelocity(this.player.body, { 
                    x: 0, 
                    y: this.player.body.velocity.y  // Keep gravity effect
                });
            }
        }
        
        // Update player
        this.player.update();
        
        if (this.gameStarted && !this.gameOver) {
            
            let firstFloater;
            this.gameObjects.forEach((gameobject, i) => {
                if(gameobject.body.label === "floater") {
                    if(!firstFloater) firstFloater = i;
                    const playerBottom = this.player.body.bounds.max.y;
                    const platformTop = gameobject.body.bounds.min.y;
   
                    if(Math.abs(playerBottom - platformTop) > 50) return;

                    if (playerBottom < platformTop + 15) {
                        // if(i === firstFloater) {
                        //     console.log(playerBottom, platformTop);
                        //     console.log("above!");
                        // }
                        gameobject.body.isSensor = false;
                    } else {
                        // if(i === firstFloater) {
                        //     console.log(playerBottom, platformTop)
                        //     console.log("below!");
                        // }

                        // Disable the collision for one-way platforms when jumping up
                        gameobject.body.isSensor = true;
                    }
                }
            })

            for (const hazard of this.hazards) {
                const pairs = Matter.Query.collides(this.player.body, [hazard.body]);
                if (!this.collidedHazards.has(hazard.body.id) && pairs.length > 0) {
                    this.collidedHazards.add(hazard.body.id);
                    this.onDeath();
                    break;
                }
            }

            for (const pickup of this.pickups) {
                const pairs = Matter.Query.collides(this.player.body, [pickup.body]);
                if (!this.collidedPickups.has(pickup.body.id) && pairs.length > 0) {
                    this.collidedPickups.add(pickup.body.id);
                    if(pickup.effect === "multiply") {
                        const stats = this.html_UI.getStats();
                        let playerScore = this.html_UI.getStats().score;
                        this.html_UI.addScore(playerScore);
                        this.player.spawnScoreEff("+2")
                    } else {
                        this.html_UI.addScore(5);
                        this.player.spawnScoreEff("+5")
                    }
                    pickup.destroy();
                    break;
                }
            }

            for (const system of this.systems) {
                const pairs = Matter.Query.collides(this.player.body, [system.body]);
                if (!this.collidedSystems.has(system.body.id) && pairs.length > 0) {
                    this.collidedSystems.add(system.body.id);
                    switch(system.name) {
                        case("boss"): 
                            let threshold = this.checkEndCollider(system, pairs);
                            system.destroy(2000, threshold);
                            break;
                        case("respawnTrig"):
                            this.respawnSpot = this.player.body.position.x;
                            console.log(this.respawnSpot);
                            system.destroy();
                            break;
                        case("recordStartTrig"):
                            console.log("recordStartTrig");
                            this.videoRecorder.resumeRecording();
                            system.destroy();
                            break;
                        case("recordEndTrig"):
                            console.log("recordEndTrig");
                            this.videoRecorder.pauseRecording();
                            system.destroy();
                            break;
                    }
                }
            }
        }
        
        // Check if player fell off screen
        if (this.gameStarted && !this.gameOver && this.player.isOffScreen()) {
            console.log("oof");
            this.onDeath();
        }
        
        // Update camera to follow player
        if (this.gameStarted) {
            const cameraX = -this.player.body.position.x + this.config.width / 3;
            this.app.stage.x = Math.min(0, cameraX);
            
            // Make sure camera background stays positioned behind the game
            if (this.cameraManager.cameraSprite) {
                // Move the camera sprite with the stage
                this.cameraManager.cameraSprite.x = this.config.width / 2 - this.app.stage.x;
            }
        }
    }

    // Finish here
    checkEndCollider(system, pairs) {
        let threshold;
        console.log("pairs: ", pairs)
        for (var i = 0; i < pairs.length; i++) {
            var pair = pairs[i];
            // console.log(system.yCoords);
            // console.log(pair.bodyA.position.y);

            system.yCoords[1] -= 50; // Increasing the ceiling value for the tower to make things line up better
            system.yCoords[0] -= 50; // Increasing the floor value for the tower to make things line up better

            let range = system.yCoords[0] - system.yCoords[1];
            let height = (pair.bodyA.position.y - system.yCoords[0]) * -1;
            let heightClamped = Math.min(Math.max(height, 0), range);

            threshold = Math.floor ( heightClamped / range * 5 ) 
            let score = threshold * 20 + 20; // 5 levels of score, each worth 20 points
            console.log("score: ", score);
            if(score < 40) score = 40; // Minimum score of 30
            this.html_UI.addScore(score);
        }
        this.endGame(true);
        return threshold;
    }

    // Add resize handling
    handleResize(width, height) {
        this.config.width = width;
        this.config.height = height;
        
        // if (this.app) this.app.renderer.resize(width, height);
        this.cameraManager.resizeCameraSprite();
    }

    cleanupLevel() {
        // Clean up level
        if (this.levelBuilder) {
            this.levelBuilder.cleanupLevel(this.gameObjects, this.hazards, this.pickups);
            this.gameObjects = [];
            this.hazards = [];
            this.pickups = [];
        }
    }

    cleanup() {
        this.collidedHazards.clear();
        this.collidedPickups.clear();
        this.collidedSystems.clear();
        // Stop the game loop
        if (this.app && this.app.ticker) {
            this.app.ticker.remove(this.gameLoop.bind(this));
            if (this.cameraTexture) {
                this.app.ticker.remove(this.updateCameraTexture.bind(this));
            }
        }
        
        if (this.cameraManager) this.cameraManager.cleanup();
        if (this.videoRecorder) this.videoRecorder.cleanup();
        if (this.player) this.player.cleanup();
        if (this.runner) Matter.Runner.stop(this.runner);
        if (this.engine) Matter.Engine.clear(this.engine);
        if (this.app) this.app.destroy(true, true);

        if (this.levelBuilder) {
            this.levelBuilder.cleanupLevel(this.gameObjects, this.hazards, this.pickups);
            this.gameObjects = [];
            this.hazards = [];
            this.pickups = [];
            this.systems = [];
        }
    }
}  

export default GameManager;